import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import moment from 'moment';
import { ApiHelpers } from '../../../helpers';

/**
 * Custom hook for booking validation and capacity calculation
 * @param {string} restaurantId - The restaurant ID to get booking settings for
 */
export const useBookingValidation = (restaurantId) => {
  const [bookingSettings, setBookingSettings] = useState(null);
  const [availableCapacity, setAvailableCapacity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  // Fetch booking settings
  useEffect(() => {
    if (!restaurantId) return;

    setLoading(true);
    ApiHelpers.getBookingSettings(restaurantId)
      .then((response) => {
        setBookingSettings(response.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load booking settings:', err);
        setError('Could not load booking settings');
        setLoading(false);
      });
  }, [restaurantId]);

  /**
   * Fetches available booking capacity for a specific date
   * @param {string} date - The date to fetch capacity for (YYYY-MM-DD)
   */
  const fetchCapacity = async (date) => {
    if (!restaurantId || !date) return;
    
    setCapacityLoading(true);
    setSelectedDate(date);
    
    try {
      const response = await ApiHelpers.getBookingCapacity(restaurantId, date);
      setAvailableCapacity(response.data);
      setCapacityLoading(false);
    } catch (err) {
      console.error('Failed to load booking capacity:', err);
      setAvailableCapacity([]);
      setCapacityLoading(false);
      message.error('Could not load available time slots');
    }
  };

  /**
   * Gets available time slots for the selected date with their capacity
   * @returns {Array} - Array of available time slots with capacity information
   */
  const getAvailableTimeSlots = useCallback(() => {
    if (!availableCapacity || availableCapacity.length === 0) return [];
    
    return availableCapacity.map(slot => ({
        time: slot.time ? slot.time.substring(0, 5) : '', // Format to HH:MM
        capacity: slot.capacity,
        disabled: slot.capacity <= 0
    }));
}, [availableCapacity]);

  /**
   * Gets maximum party size for a specific time slot
   * @param {string} timeSlot - The time slot to check (HH:MM)
   * @returns {number} - Maximum party size for the time slot
   */
  const getMaxPartySize = (timeSlot) => {
    if (!availableCapacity || availableCapacity.length === 0 || !timeSlot) return 20;
    
    // Find the time slot in available capacity
    const slot = availableCapacity.find(slot => 
      slot.time && slot.time.substring(0, 5) === timeSlot
    );
    
    if (slot) {
      return slot.capacity;
    }
    
    // If no specific capacity found, use max party size from booking settings
    if (bookingSettings && bookingSettings.maxPartySize) {
      return bookingSettings.maxPartySize;
    }
    
    return 20; // Default fallback
  };

  /**
   * Validates if a booking meets capacity requirements
   * @param {object} booking - The booking to validate
   * @returns {boolean} - Whether the booking is valid
   */
  const validateCapacity = (booking) => {
    if (!availableCapacity || availableCapacity.length === 0) return true;
    
    const { hourStart, partySize } = booking;
    if (!hourStart || !partySize) return false;
    
    // Format time for comparison
    const timeFormatted = hourStart.substring(0, 5);
    
    // Find the time slot in available capacity
    const slot = availableCapacity.find(slot => 
      slot.time && slot.time.substring(0, 5) === timeFormatted
    );
    
    if (!slot) {
      message.error(`Time slot ${timeFormatted} is not available`);
      return false;
    }
    
    if (partySize > slot.capacity) {
      message.error(`Maximum party size for ${timeFormatted} is ${slot.capacity}`);
      return false;
    }
    
    return true;
  };

  /**
   * Validates if a booking falls within allowed time slots
   * @param {object} booking - The booking to validate
   * @returns {boolean} - Whether the booking is valid
   */
  const validateTimeSlots = (booking) => {
    if (!bookingSettings || !booking) return false;
  
    const { reservationDate, hourStart, hourEnd } = booking;
    
    // Validate that date and times are defined
    if (!reservationDate || !hourStart || !hourEnd) {
      message.error('Date and time must be specified');
      return false;
    }
  
    // Get day of week from the reservation date as a number (0-6)
    const bookingDate = moment(reservationDate);
    const dayIndex = bookingDate.day(); // Returns 0-6 (Sunday is 0)
    
    // Map the numeric index to the property name in the settings object
    let dayProperty = '';
    switch(dayIndex) {
        case 0: dayProperty = 'sunday'; break;
        case 1: dayProperty = 'monday'; break;
        case 2: dayProperty = 'tuesday'; break;
        case 3: dayProperty = 'wednesday'; break;
        case 4: dayProperty = 'thursday'; break;
        case 5: dayProperty = 'friday'; break;
        case 6: dayProperty = 'saturday'; break;
        default: dayProperty = '';
    }
  
    // Get day settings from days object
    const daySettings = bookingSettings.days ? bookingSettings.days[dayProperty] : null;
    
    // Check if the day is enabled for bookings
    if (!daySettings) {
      message.error(`This day has no booking settings configured`);
      return false;
    }
    
    if (daySettings.enabled === false) {
      message.error(`Reservations are not available on this day`);
      return false;
    }
    
    if (!daySettings.timeSlots || daySettings.timeSlots.length === 0) {
      message.error(`No time slots are available on this day`);
      return false;
    }
  
    // Check if booking fits in any of the allowed time slots
    // Make sure we're working with string times in the right format
    let startTimeString = hourStart;
    let endTimeString = hourEnd;
    
    // First convert dates to strings if needed
    if (typeof hourStart === 'object' && hourStart.format) {
      startTimeString = hourStart.format('HH:mm');
    }
    
    if (typeof hourEnd === 'object' && hourEnd.format) {
      endTimeString = hourEnd.format('HH:mm');
    }
    
    // Now create moment objects for comparison
    const startTime = moment(startTimeString, 'HH:mm');
    const endTime = moment(endTimeString, 'HH:mm');
  
    // Validate end time is after start time
    if (!endTime.isAfter(startTime)) {
      message.error('End time must be after start time');
      return false;
    }
  
    // Check if booking fits in any of the allowed time slots
    const isInAllowedTimeSlot = daySettings.timeSlots.some(slot => {
      // Ensure we're parsing time strings correctly
      const slotStartTimeString = slot.startTime || '00:00:00';
      const slotEndTimeString = slot.endTime || '00:00:00';
      
      // Strip milliseconds if present
      const cleanStartTime = slotStartTimeString.split('.')[0];
      const cleanEndTime = slotEndTimeString.split('.')[0];
      
      const slotStartTime = moment(cleanStartTime, 'HH:mm:ss');
      const slotEndTime = moment(cleanEndTime, 'HH:mm:ss');
      
      // Check if booking start and end times are within the slot
      const startInSlot = startTime.isSameOrAfter(slotStartTime);
      const endInSlot = endTime.isSameOrBefore(slotEndTime);
      
      return startInSlot && endInSlot;
    });
  
    if (!isInAllowedTimeSlot) {
      message.error('The reservation time is outside of available hours');
      return false;
    }
  
    return true;
  };

  /**
   * Validates if a booking meets party size requirements
   * @param {object} booking - The booking to validate
   * @returns {boolean} - Whether the booking is valid
   */
  const validatePartySize = (booking) => {
    if (!bookingSettings || !booking) return false;
  
    const { reservationDate, hourStart, partySize } = booking;
    
    if (!reservationDate || !hourStart || !partySize) {
      message.error('Date, time, and party size must be specified');
      return false;
    }
  
    // First check against capacity data
    if (availableCapacity && availableCapacity.length > 0) {
      // Format time for comparison
      const timeFormatted = typeof hourStart === 'object' && hourStart.format ? 
        hourStart.format('HH:mm') : hourStart.substring(0, 5);
      
      // Find the time slot in available capacity
      const slot = availableCapacity.find(slot => 
        slot.time && slot.time.substring(0, 5) === timeFormatted
      );
      
      if (slot && partySize > slot.capacity) {
        message.error(`Maximum party size for ${timeFormatted} is ${slot.capacity}`);
        return false;
      }
    }
  
    // Fall back to day settings if no capacity data
    const bookingDate = moment(reservationDate);
    const dayIndex = bookingDate.day();
    
    let dayProperty = '';
    switch(dayIndex) {
        case 0: dayProperty = 'sunday'; break;
        case 1: dayProperty = 'monday'; break;
        case 2: dayProperty = 'tuesday'; break;
        case 3: dayProperty = 'wednesday'; break;
        case 4: dayProperty = 'thursday'; break;
        case 5: dayProperty = 'friday'; break;
        case 6: dayProperty = 'saturday'; break;
        default: dayProperty = '';
    }
  
    const daySettings = bookingSettings.days ? bookingSettings.days[dayProperty] : null;
    if (!daySettings || !daySettings.enabled) return false;
  
    // Format start time for comparison
    const startTime = typeof hourStart === 'object' && hourStart.format ? 
      moment(hourStart.format('HH:mm'), 'HH:mm') : moment(hourStart, 'HH:mm');
  
    // Find the applicable time slot
    const applicableSlot = daySettings.timeSlots.find(slot => {
      const slotStartTimeString = slot.startTime || '00:00:00';
      const slotEndTimeString = slot.endTime || '00:00:00';
      
      const cleanStartTime = slotStartTimeString.split('.')[0];
      const cleanEndTime = slotEndTimeString.split('.')[0];
      
      const slotStartTime = moment(cleanStartTime, 'HH:mm:ss');
      const slotEndTime = moment(cleanEndTime, 'HH:mm:ss');
      
      return startTime.isSameOrAfter(slotStartTime) && startTime.isBefore(slotEndTime);
    });
  
    if (!applicableSlot) return false;
  
    // Check if party size is within the allowed range
    if (partySize > applicableSlot.maxPartySize) {
      message.error(`The maximum party size for this time slot is ${applicableSlot.maxPartySize}`);
      return false;
    }
  
    return true;
  };

  /**
   * Validates if a booking meets advance booking requirements
   * @param {object} booking - The booking to validate
   * @returns {boolean} - Whether the booking is valid
   */
  const validateAdvanceBooking = (booking) => {
    if (!bookingSettings || !booking) return false;

    const { reservationDate } = booking;
    if (!reservationDate) return false;

    const bookingDate = moment(reservationDate);
    const today = moment().startOf('day');
    const diffDays = bookingDate.diff(today, 'days');
    
    // Check if booking is too far in advance
    if (diffDays > bookingSettings.maxAdvanceDays) {
      message.error(`Reservations can only be made up to ${bookingSettings.maxAdvanceDays} days in advance`);
      return false;
    }
    
    // Check if booking meets minimum advance hours requirement
    if (booking.hourStart) {
      const hourStartFormatted = typeof booking.hourStart === 'object' && booking.hourStart.format ?
        booking.hourStart.format('HH:mm') : booking.hourStart;
      
      const bookingDateTime = moment(`${reservationDate}T${hourStartFormatted}`);
      const diffHours = bookingDateTime.diff(moment(), 'hours', true);
      
      if (diffHours < bookingSettings.minAdvanceHours) {
        message.error(`Reservations must be made at least ${bookingSettings.minAdvanceHours} hours in advance`);
        return false;
      }
    }

    return true;
  };

  /**
   * Comprehensive validation for bookings
   * @param {object} booking - The booking to validate
   * @returns {boolean} - Whether the booking is valid
   */
  const validateBooking = (booking) => {
    if (loading) {
      message.info('Loading booking settings...');
      return false;
    }
    
    if (error) {
      message.error(error);
      return false;
    }

    // Check that we have the required nested structure
    if (!bookingSettings || !bookingSettings.days) {
      message.error('Booking settings are not properly configured');
      return false;
    }

    // Run all validations
    return validateTimeSlots(booking) && 
           validatePartySize(booking) && 
           validateAdvanceBooking(booking) &&
           validateCapacity(booking);
  };

  return {
    validateBooking,
    validateTimeSlots,
    validatePartySize,
    validateAdvanceBooking,
    validateCapacity,
    fetchCapacity,
    getAvailableTimeSlots,
    getMaxPartySize,
    bookingSettings,
    availableCapacity,
    loading,
    capacityLoading,
    error,
    selectedDate
  };
};